/** Hydrate productSearch hits via getProducts for Range/search PLP tiles. */
import {useMemo} from 'react';

import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';
import {useQuery} from '@tanstack/react-query';

import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';
import {
  chunk,
  GET_TILE_HYDRATE_PAGE_SIZE,
  GET_TILE_HYDRATE_PARAMS,
} from '@amblique/king-living/app/utils/api-utils';

function mapHitsToTileProducts(hits, products) {
  if (!hits?.length || !products?.length) return [];

  const byId = new Map(products.map((p) => [String(p.id ?? p.productId), p]));

  return hits
    .map((hit) => byId.get(String(hit?.productId ?? hit?.id ?? '')))
    .filter(Boolean);
}

/**
 * @param {object[]|undefined} hits
 * @returns {object[]}
 */
export function useMergeSearchHitShortDescriptions(hits) {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const inventoryIds = useShopperInventoryIds();

  const productIds = useMemo(() => {
    const ids = new Set();

    for (const hit of hits ?? []) {
      const id = hit?.productId ?? hit?.id;

      if (id) ids.add(String(id));
    }

    return [...ids];
  }, [hits]);

  const idsKey = productIds.join(',');

  const {data: products = []} = useQuery({
    queryKey: ['king-living/range-plp-shortDescription', idsKey, inventoryIds],
    queryFn: async () => {
      if (productIds.length === 0) return [];

      const token = await getTokenWhenReady();
      const chunks = chunk(productIds, GET_TILE_HYDRATE_PAGE_SIZE);
      const results = await Promise.all(
        chunks.map((ids) => {
          return api.shopperProducts
            .getProducts({
              parameters: {
                ids: ids.join(','),
                ...GET_TILE_HYDRATE_PARAMS,
                ...(inventoryIds ? {inventoryIds} : {}),
              },
              headers: {Authorization: `Bearer ${token}`},
            })
            .then((res) => res?.data ?? []);
        })
      );

      return results.flat();
    },
    enabled: productIds.length > 0,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => mapHitsToTileProducts(hits, products), [hits, products]);
}

export default useMergeSearchHitShortDescriptions;
